// DEPRECATED: Prefer using the useNotificationsQuery() hook from app/src/hooks/useNotifications.ts directly.
// This context is kept for backward compatibility.

import React, { createContext, useContext, ReactNode, useCallback, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { notificationService, type NotificationItem } from "@/services/notificationService";
import { toast } from "sonner";

export interface AppNotification {
  id: string;
  type: string;
  title: string;
  description: string;
  time: string;
  read: boolean;
  link?: string | null;
  relatedInitiativeId?: string | null;
}

interface NotificationContextType {
  notifications: AppNotification[];
  unreadCount: number;
  addNotification: (notification: { title: string; description: string; type: string; [key: string]: any }) => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);
const TOASTABLE_TYPES = new Set([
  "idea_intake_ready",
  "idea_intake_needs_confirmation",
  "idea_processing_complete",
  "idea_processing_duplicate",
  "idea_processing_needs_revision",
  "idea_processing_failed",
]);

function mapNotification(n: NotificationItem): AppNotification {
  let time = "";
  if (n.created_at) {
    const d = new Date(n.created_at);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) time = "Just now";
    else if (mins < 60) time = `${mins} min ago`;
    else if (mins < 1440) time = `${Math.floor(mins / 60)} hr ago`;
    else time = `${Math.floor(mins / 1440)} day(s) ago`;
  }
  return {
    id: n.id,
    type: n.type,
    title: n.title,
    description: n.message,
    time,
    read: n.is_read,
    link: n.link,
    relatedInitiativeId: n.related_initiative_id,
  };
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { hasTenant } = useAuth() as { hasTenant: boolean };
  const queryClient = useQueryClient();
  const seenNotificationIdsRef = useRef<Set<string>>(new Set());

  const { data: notifications = [] } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => notificationService.list(false, 50).then((items) => items.map(mapNotification)),
    enabled: hasTenant,
    refetchInterval: 30000,
  });

  const { data: countData } = useQuery({
    queryKey: ["notifications-unread-count"],
    queryFn: () => notificationService.unreadCount(),
    enabled: hasTenant,
    refetchInterval: 30000,
  });

  const unreadCount = countData?.count ?? notifications.filter((n) => !n.read).length;

  useEffect(() => {
    const nextIds = new Set(notifications.map((notification) => notification.id));

    if (seenNotificationIdsRef.current.size === 0) {
      seenNotificationIdsRef.current = nextIds;
      return;
    }

    notifications
      .filter(
        (notification) =>
          !seenNotificationIdsRef.current.has(notification.id) &&
          TOASTABLE_TYPES.has(notification.type)
      )
      .slice(0, 3)
      .forEach((notification) => {
        toast(notification.title, {
          description: notification.description,
          action: notification.link
            ? {
                label: "Open",
                onClick: () => window.location.assign(notification.link as string),
              }
            : undefined,
        });
      });

    seenNotificationIdsRef.current = nextIds;
  }, [notifications]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["notifications"] });
    queryClient.invalidateQueries({ queryKey: ["notifications-unread-count"] });
  };

  const markReadMutation = useMutation({
    mutationFn: (id: string) => notificationService.markRead(id),
    onSuccess: invalidate,
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => notificationService.markAllRead(),
    onSuccess: invalidate,
  });

  const markRead = useCallback((id: string) => markReadMutation.mutate(id), [markReadMutation]);
  const markAllRead = useCallback(() => markAllReadMutation.mutate(), [markAllReadMutation]);

  // No-op for backward compat — real notifications are server-driven
  const addNotification = useCallback((_notification: { title: string; description: string; type: string; [key: string]: any }) => {}, []);

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, addNotification, markRead, markAllRead }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotifications must be used within NotificationProvider");
  return ctx;
}
